import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { OfflineProvider } from './components/OfflineProvider';
import { ConfirmProvider } from './components/ConfirmProvider';
import { SessionProvider } from './components/SessionProvider';
import { initOutboxAutoSync } from './lib/outbox';
import { initSentry, SentryErrorBoundary } from './lib/sentry';
import { hideNativeSplashScreen } from './lib/nativeShell';
import './styles/globals.css';
import { BRAND } from './config/brand';

// Initialise Sentry BEFORE any other code runs — gives us coverage of
// initialisation errors that would otherwise fail silently. No-ops if
// VITE_SENTRY_DSN isn't set (local dev, branch previews, etc.).
initSentry();

// Set the document title from brand config.
document.title = BRAND.name;

// Boot the write-outbox sync engine. Listens for online/focus events and
// drains queued sign-ins / forms when signal returns. Safe to call once
// at startup — guarded internally against double-init.
initOutboxAutoSync();

// Fallback UI for unrecoverable React render errors. The Sentry error
// boundary captures the exception, then shows this card. We expose a
// "Refresh app" button because the most common cause is a stale-bundle
// chunk-load after a redeploy — a hard refresh fixes it.
function FatalErrorCard() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      background: '#FAF7F2',
      color: '#1E293B',
    }}>
      <div style={{
        maxWidth: 420,
        background: '#fff',
        borderRadius: 12,
        padding: 24,
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
        <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Something went wrong.</h2>
        <p style={{ margin: '0 0 16px', color: '#475569', fontSize: 14, lineHeight: 1.5 }}>
          The app hit an unexpected error. We've been notified automatically.
          Refreshing usually fixes it — most often this is just a stale page
          after we shipped an update.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            background: '#1E293B',
            color: '#fff',
            border: 0,
            borderRadius: 8,
            padding: '10px 18px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Refresh app
        </button>
      </div>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <SentryErrorBoundary fallback={<FatalErrorCard />} showDialog={false}>
      <BrowserRouter>
        <SessionProvider>
          <ConfirmProvider>
            <App />
            <OfflineProvider />
          </ConfirmProvider>
        </SessionProvider>
      </BrowserRouter>
    </SentryErrorBoundary>
  </React.StrictMode>
);

// After the first paint, tell the native Capacitor shell to hide its splash
// screen. On web (PWA) this is a no-op. We defer to next tick so React's
// initial commit has actually painted before we hide — otherwise the user
// sees a flash of blank white between splash and the rendered shell.
queueMicrotask(() => hideNativeSplashScreen());
